// Zoo lab (dev page): both habitats with all 19 creatures, driven by a fake clock that loops a
// hard-coded 2-bar pattern at 125 BPM (no audio), plus buttons that fire live hits.
import { useEffect, useMemo, useRef, useState } from "react";
import type { NoteEvent } from "@eb/shared";
import { useTitle } from "../lib/router";
import { speciesForSound, ZooStage, zooHit, type ZooClock, type ZooScene } from "../zoo";
import { creatureSeed } from "../zoo/lib/rng";

const BAR = 3840;
const LENGTH = 2 * BAR;
const S16 = 240;

const JUNGLE_SOUNDS: [string, string | null][] = [
  ["Kick", null],
  ["Snare", null],
  ["Rim", null],
  ["Clap", null],
  ["Hat", null],
  ["Open Hat", null],
  ["Shaker", null],
  ["Low Tom", null],
  ["Hi Tom", null],
  ["808", null],
  ["Sub Hit", null],
  ["Pluck", null],
  ["Stab", null],
  ["Blip", null],
  ["Riser", null],
  ["Impact", null],
  ["Bass", "mono"],
  ["Chords", "poly"],
  ["Lead", "acid"],
];

const DEEP_SEA_SOUNDS: [string, string | null][] = [
  ["Kick", null],
  ["Kick Low", null],
  ["Kick Short", null],
  ["Kick Click", null],
  ["Snare", null],
  ["Snare Tight", null],
  ["Clap", null],
  ["Clap Stack", null],
  ["Snap", null],
  ["Hat", null],
  ["Hat Tick", null],
  ["Hat Loose", null],
  ["Open Hat", null],
  ["Rim", null],
  ["Wood Perc", null],
  ["Crash", null],
  ["Bass", "bass808"],
  ["Organ", "organ"],
  ["Lead", "fm"],
];

function scene(habitat: ZooScene["habitat"], sounds: [string, string | null][]): ZooScene {
  return {
    habitat,
    creatures: sounds.map(([name, model], slot) => ({
      slot,
      name,
      species: speciesForSound(name, slot, model),
      seed: creatureSeed(`lab-${habitat}`, slot),
    })),
  };
}

function ev(pad: number, tick: number, velocity: number, length = S16, note = 60): NoteEvent {
  return { pad, tick, note, velocity, length_ticks: length };
}

/** A 2-bar groove touching every slot: accents (≥110), long notes (holds) and chords. */
function pattern(): NoteEvent[] {
  const e: NoteEvent[] = [];
  for (let bar = 0; bar < 2; bar++) {
    const b = bar * BAR;
    e.push(ev(0, b, 120), ev(0, b + 10 * S16, 96), ev(0, b + 8 * S16, 104));
    e.push(ev(1, b + 4 * S16, 100), ev(1, b + 12 * S16, bar === 1 ? 124 : 100));
    for (let i = 0; i < 8; i++) e.push(ev(4, b + i * 2 * S16, i % 2 ? 64 : 92));
    e.push(ev(5, b + 14 * S16, 88, 2 * S16));
    for (let i = 1; i < 16; i += 4) e.push(ev(6, b + i * S16, 70));
    e.push(ev(9, b + 0, 100, 6 * S16));
    e.push(ev(11, b + 3 * S16, 90), ev(11, b + 11 * S16, 84));
    e.push(ev(13, b + 7 * S16, 80));
    // Bass: long notes (hold); chords: half-bar pads; lead: a little figure.
    e.push(ev(16, b, 105, 3 * S16, 36), ev(16, b + 6 * S16, 100, 6 * S16, 43));
    e.push(
      ev(17, b + 2 * S16, 90, 6 * S16, 60),
      ev(17, b + 2 * S16, 90, 6 * S16, 64),
      ev(17, b + 10 * S16, 112, 6 * S16, 67),
    );
    for (const [i, n] of [
      [0, 72],
      [3, 74],
      [6, 76],
      [9, 79],
    ])
      e.push(ev(18, b + i * S16 + (bar ? 2 * S16 : 0), 96, 2 * S16, n));
  }
  e.push(ev(2, 7 * S16, 90), ev(2, BAR + 15 * S16, 90));
  e.push(ev(3, BAR + 4 * S16, 110), ev(3, BAR + 12 * S16, 116));
  e.push(ev(7, BAR + 13 * S16, 100), ev(7, BAR + 14 * S16, 104), ev(8, BAR + 15 * S16, 112));
  e.push(ev(10, BAR + 8 * S16, 100, 4 * S16), ev(12, BAR + 6 * S16, 108));
  e.push(ev(14, BAR + 8 * S16, 90, 8 * S16), ev(15, 0, 124, 8 * S16));
  e.push(ev(8, 15 * S16, 96), ev(14, 4 * S16, 70));
  return e.sort((a, b) => a.tick - b.tick);
}

/** 0.5 ms per tick at 125 BPM, looping from when it was created. */
function fakeClock(isPlaying: () => boolean): ZooClock {
  const start = performance.now();
  const events = pattern();
  return {
    position: () => ({
      tick: Math.floor((performance.now() - start) * 2) % LENGTH,
      lengthTicks: LENGTH,
      playing: isPlaying(),
    }),
    events: () => events,
    level: () => (isPlaying() ? 0.55 : 0),
  };
}

export function LabZoo() {
  useTitle("Zoo lab");
  const [playing, setPlaying] = useState(true);
  const [calm, setCalm] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  const playingRef = useRef(playing);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);
  const clock = useMemo(() => fakeClock(() => playingRef.current), []);
  const jungle = useMemo(() => scene("jungle", JUNGLE_SOUNDS), []);
  const deepSea = useMemo(() => scene("deep_sea", DEEP_SEA_SOUNDS), []);

  return (
    <main className="page lab-zoo" style={{ display: "grid", gap: 16, padding: 16, maxWidth: 1280, margin: "0 auto" }}>
      <h1 style={{ margin: 0 }}>Zoo lab</h1>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <button type="button" onClick={() => setPlaying((p) => !p)} aria-pressed={playing}>
          {playing ? "Stop" : "Play"}
        </button>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={calm} onChange={(e) => setCalm(e.target.checked)} />
          Calm motion
        </label>
      </div>
      <section aria-label="Live hits" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {JUNGLE_SOUNDS.map(([name], slot) => (
          <button key={slot} type="button" onClick={() => zooHit(slot, 100)} onDoubleClick={() => zooHit(slot, 127)}>
            {slot + 1}. {name}
          </button>
        ))}
        <button type="button" onClick={() => JUNGLE_SOUNDS.forEach((_, slot) => zooHit(slot, 127))}>
          All (accent)
        </button>
      </section>
      <ZooStage
        scene={jungle}
        clock={clock}
        calm={calm}
        height={340}
        label="Jungle habitat: 19 sound creatures dancing to the loop"
      />
      <ZooStage
        scene={deepSea}
        clock={clock}
        calm={calm}
        height={340}
        label="Deep-sea habitat: 19 sound creatures dancing to the loop"
      />
    </main>
  );
}
